import { useContext, useEffect, useMemo } from 'react';

import { Button, useDisclosure } from '@chakra-ui/react';
import { Formik } from 'formik';
import { isEqual } from 'lodash';
import { MdOutlineRequestQuote } from 'react-icons/md';

import { TermEnum } from '../../gql/financialInfoGql';

import Modal from '../shared/Modal';

import PaymentEstimateForm from './PaymentEstimateForm';

import { DealActionsEnum, DealContext } from '../../libs/DealContext';
import { ModalContext } from '../../libs/contextLib';

export type PaymentEstimateValues = {
  term: number;
  money_down: number;
};

const PaymentEstimateModal = () => {
  const { deal, dispatch } = useContext(DealContext);
  const { modals, setModal } = useContext(ModalContext);

  const disclosure = useDisclosure();

  const initialValues = useMemo(
    () => ({
      term: deal.financial_info?.term ?? TermEnum.SeventyTwo,
      money_down: deal.financial_info?.money_down ?? 0,
    }),
    [deal.financial_info?.term, deal.financial_info?.money_down],
  );

  const handleOnClick = () => {
    setModal({
      PaymentEstimator: true,
    });

    disclosure.onOpen();
  };

  const handleSave = (values: PaymentEstimateValues) => {
    dispatch({
      type: DealActionsEnum.UpdateDeal,
      payload: {
        ...deal,
        financial_info: {
          ...deal.financial_info,
          term: values.term,
          money_down: values.money_down,
        },
      },
    });

    disclosure.onClose();
  };

  useEffect(() => {
    if (modals.PaymentEstimator) {
      // Trigger autosave when `PaymentEstimator` modal is opened and term or money down are changed.
      setModal({ PaymentEstimator: false });
    }
  }, [deal.financial_info?.term, deal.financial_info?.money_down]);

  return (
    <>
      <Button
        variant="secondary"
        size="smWithIconLeft"
        leftIcon={<MdOutlineRequestQuote size={16} />}
        onClick={handleOnClick}
      >
        Payment Estimate
      </Button>
      {disclosure.isOpen && (
        <Formik initialValues={initialValues} onSubmit={handleSave}>
          {({ values, handleSubmit }) => (
            <Modal
              {...disclosure}
              title="Payment Estimate"
              showDivider={false}
              leftButtons={
                <Button variant="warning" onClick={disclosure.onClose}>
                  Close
                </Button>
              }
              rightButtons={
                <Button
                  type="submit"
                  isDisabled={isEqual(initialValues, values)}
                  onClick={() => handleSubmit()}
                >
                  Save
                </Button>
              }
            >
              <PaymentEstimateForm deal={deal} />
            </Modal>
          )}
        </Formik>
      )}
    </>
  );
};

export default PaymentEstimateModal;
